import { useEffect, useState } from "react";

/**
 * overlayBehavior — infraestructura compartida de Drawer y Modal (ui-v2.5). Interno (no en barril).
 * Resuelve lo que suele quedar al 70%: foco atrapado, retorno de foco, bloqueo de scroll sin salto
 * de layout, y Escape que cierra SOLO el overlay de encima cuando hay varios apilados.
 */

// —— Bloqueo del scroll del body con recuento (soporta varios overlays a la vez) ——
let lockCount = 0;
let saved = { overflow: "", paddingRight: "" };

function lockScroll() {
  if (lockCount === 0) {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    saved = { overflow: document.body.style.overflow, paddingRight: document.body.style.paddingRight };
    document.body.style.overflow = "hidden";
    if (scrollbar > 0) {
      const current = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${current + scrollbar}px`; // compensa la barra → sin salto
    }
  }
  lockCount += 1;
}

function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.style.overflow = saved.overflow;
    document.body.style.paddingRight = saved.paddingRight;
  }
}

// —— Pila de Escape: solo el de encima responde ——
const escapeStack = [];
function handleGlobalKey(e) {
  if (e.key === "Escape" && escapeStack.length) {
    escapeStack[escapeStack.length - 1]();
  }
}
if (typeof document !== "undefined") {
  document.addEventListener("keydown", handleGlobalKey);
}

function getFocusable(container) {
  if (!container) return [];
  const selector =
    'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll(selector));
}

// —— Offset del NavBar fijo de la app ——
// El NavBar (`[data-testid="app-navbar"]`, fixed, z-1050) taparía la fila superior de un overlay que
// arranque en top:0. Medimos su altura REAL (como el Drawer de inventario, el precedente de la app) y
// la devolvemos para desplazar el overlay: así el chrome (NavBar y sidebar) sigue visible y usable —
// el Drawer/Modal es un panel de detalle, no un modo a pantalla completa. Fallback 48px (alto real
// aproximado con `px-2 py-1`) hasta la primera medición. En móvil el NavBar está display:none → 0.
const NAVBAR_FALLBACK = 48;

/**
 * useNodoChrome — engancha un nodo del chrome de la app (NavBar, sidebar) AUNQUE TODAVÍA NO ESTÉ,
 * y lo re-mide mientras exista.
 *
 * ── POR QUÉ HIZO FALTA ────────────────────────────────────────────────────────────────────
 *
 * Los dos hooks de offset hacían `querySelector` UNA VEZ al montar y, si no encontraban el nodo,
 * se rendían: `if (!nav) return undefined`. Funcionaba por accidente — el overlay se montaba
 * dentro del árbol de la página, y para entonces el NavBar llevaba rato en el DOM.
 *
 * Al pasar Modal y Drawer a portal, el overlay se monta en `document.body` en el mismo ciclo en
 * que React inserta el portal, y esa medición llega ANTES. Resultado: el NavBar no se encontraba
 * y el offset se quedaba en el respaldo de 48 para siempre — el overlay se metía 16px BAJO la
 * barra. El respaldo nunca fue una decisión: era un número que se usaba hasta la primera medida.
 *
 * ── LO QUE ESTE AYUDANTE GARANTIZA ────────────────────────────────────────────────────────
 *
 *   · si el nodo ya está, se mide ahora
 *   · si NO está, se espera a que aparezca — y al aparecer se le engancha TAMBIÉN el
 *     ResizeObserver, para no perder el recálculo que ya existía (el NavBar cambia de alto, y en
 *     móvil pasa a display:none → altura 0, que es el valor correcto)
 *   · y si nunca aparece, **deja de esperar**: no gira indefinidamente
 */
const ESPERA_MAXIMA_MS = 5000;

function useNodoChrome(selector, medir, inicial) {
  const [valor, setValor] = useState(inicial);
  useEffect(() => {
    let vivo = true;
    let ro;
    let mo;
    let timer;

    const soltarRO = () => { if (ro) { ro.disconnect(); ro = undefined; } };

    // Resuelve el valor con lo que HAYA ahora mismo.
    //
    // Si el nodo NO existe, el valor correcto es 0, no el respaldo: si no hay NavBar, no hay nada
    // bajo lo que colocarse. En móvil el NavBar **no está oculto: no se renderiza**, así que sin
    // esto el overlay se quedaba con el respaldo de 48 y dejaba una franja muerta arriba.
    //
    // El respaldo sigue sirviendo para lo que se creó: el primer pintado en escritorio, antes de
    // medir, donde el NavBar sí está y no queremos un salto de 0 a su altura.
    const resolver = () => {
      if (!vivo) return;
      const nodo = document.querySelector(selector);
      soltarRO();
      // Sin nodo: 0, y se queda ESPERANDO por si aparece. Es lo que ocurre al volver de movil a
      // escritorio: el evento `resize` llega ANTES de que React vuelva a pintar el NavBar, asi
      // que resolver en ese instante no lo encuentra. El observador — acotado, no indefinido —
      // lo recoge en cuanto se monta.
      if (!nodo) { setValor(0); armar(); return; }
      const remedir = () => { if (vivo) setValor(medir(nodo)); };
      remedir();
      if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(remedir);
        ro.observe(nodo);
      }
    };

    // Espera acotada a que el nodo aparezca. Se re-arma cada vez que `resolver` no lo encuentra
    // —al arrancar y al cruzar la media query— y siempre caduca: un observador colgado del body
    // indefinidamente es caro y, si el nodo no va a existir, no sirve de nada.
    function armar() {
      if (mo || typeof MutationObserver === "undefined") return;
      mo = new MutationObserver(() => {
        if (!document.querySelector(selector)) return;
        mo.disconnect(); mo = undefined;
        clearTimeout(timer);
        resolver();
      });
      mo.observe(document.body, { childList: true, subtree: true });
      clearTimeout(timer);
      timer = setTimeout(() => { if (mo) { mo.disconnect(); mo = undefined; } }, ESPERA_MAXIMA_MS);
    }

    resolver();
    window.addEventListener("resize", resolver);

    return () => {
      vivo = false;
      soltarRO();
      if (mo) mo.disconnect();
      clearTimeout(timer);
      window.removeEventListener("resize", resolver);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector]);
  return valor;
}

export function useNavbarOffset() {
  // La altura REAL del NavBar. En móvil está display:none → 0, que es el valor correcto.
  return useNodoChrome('[data-testid="app-navbar"]', (nav) => nav.getBoundingClientRect().height, NAVBAR_FALLBACK);
}

/**
 * useSidebarOffset — ancho REAL del chrome lateral izquierdo (`#mySidebar`, fixed, z-1100), medido en
 * runtime, para que un overlay a ancho completo (velo/panel) empiece DONDE EMPIEZA EL CONTENIDO y no
 * quede tapado por el sidebar. Simétrico a useNavbarOffset (que descuenta el alto del NavBar): aquí se
 * descuenta el ANCHO del sidebar por la izquierda. Sin número mágico: se mide el nodo.
 *
 * - Solo desktop: en ≤1024px el sidebar es barra SUPERIOR a ancho completo (isMobile=maxWidth:1024 en
 *   App.js/Navbar.js; el CSS .sidebar cambia en max-width:1024 / min-width:1025 — misma frontera). Ahí
 *   no hay rail izquierdo → offset 0. Se gatea con matchMedia('(min-width:1025px)') + guarda por medición
 *   (solo si el nodo está anclado a la izquierda y ocupa menos de medio viewport).
 * - Reacciona al COLAPSO/EXPANSIÓN con el overlay abierto: el ancho transiciona (.5s) y el ResizeObserver
 *   dispara por frame → recentrado suave. `resize` y el cambio de media-query también recalculan.
 * - Fallback 0 si el nodo no existe → comportamiento previo (centrado a ancho completo) intacto.
 */
export function useSidebarOffset() {
  // Mismo fallo latente que el NavBar: si `#mySidebar` no estaba al montar, se quedaba en 0 para
  // siempre y el overlay se pintaba bajo el rail. Ahora también espera a que aparezca.
  return useNodoChrome('#mySidebar', (el) => {
    const mq = window.matchMedia('(min-width: 1025px)');
    if (!mq.matches) return 0;   // ≤1024px: el sidebar es barra superior, no rail izquierdo
    const rect = el.getBoundingClientRect();
    // Sólo un rail anclado a la izquierda, no la barra móvil a ancho completo.
    return rect.left <= 1 && rect.width < window.innerWidth / 2 ? rect.width : 0;
  }, 0);
}

// —— Presencia con animación de entrada/salida (monta, pinta a "shown", desmonta tras la transición) ——
export function usePresence(open, duration = 320) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    let raf1;
    let raf2;
    let timer;
    if (open) {
      setMounted(true);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setShown(true));
      });
    } else {
      setShown(false);
      timer = setTimeout(() => setMounted(false), duration);
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timer);
    };
  }, [open, duration]);
  return { mounted, shown };
}

/**
 * useOverlay — mientras `open`, atrapa el foco en `panelRef`, bloquea el scroll, registra el
 * Escape en la pila y, al cerrar, restaura el scroll y devuelve el foco al elemento que abrió.
 * `initialFocusRef` fuerza el primer foco (p. ej. el botón Cancelar de un Modal destructivo).
 */
export function useOverlay({ active, onClose, panelRef, initialFocusRef }) {
  useEffect(() => {
    // `active` = open && panel MONTADO. Clave: si dependiéramos solo de `open`, el efecto correría
    // antes de que usePresence monte el panel (panelRef.current = null) y no re-ejecutaría al montar.
    if (!active) return undefined;
    const opener = document.activeElement;
    lockScroll();

    // Foco inicial SÍNCRONO (el panel ya está en el DOM porque `active` incluye `mounted`).
    // Destino: initialFocusRef → primer CAMPO de formulario → primer enfocable ÚTIL (no la X de
    // cerrar, marcada con data-overlay-close) → el panel. Así en un formulario el foco cae en el
    // campo y no en la X de la cabecera. preventScroll: el panel del Drawer entra desde fuera de
    // pantalla; sin esto, enfocar dentro provocaría scroll de la página.
    const panel = panelRef.current;
    const field = panel?.querySelector(
      'input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled])'
    );
    const focusables = getFocusable(panel);
    const target =
      initialFocusRef?.current ||
      field ||
      focusables.find((f) => !f.hasAttribute("data-overlay-close")) ||
      focusables[0] ||
      panel;
    target?.focus?.({ preventScroll: true });

    // El foco PROGRAMÁTICO no dispara :focus-visible (heurística del navegador), así que el anillo
    // del ui-v2 Button no se vería en el destructivo. Forzamos un anillo visible con box-shadow inline
    // sobre el destino y lo quitamos en cuanto el usuario interactúa (teclado o ratón). Sin tocar Button.
    let clearRing = () => {};
    if (target && target.style && target !== panel) {
      const prevShadow = target.style.boxShadow;
      target.style.boxShadow = "0 0 0 3px rgba(0,102,179,.45)";
      clearRing = () => {
        target.style.boxShadow = prevShadow;
        document.removeEventListener("pointerdown", clearRing, true);
        document.removeEventListener("keydown", clearRing, true);
      };
      document.addEventListener("pointerdown", clearRing, true);
      document.addEventListener("keydown", clearRing, true);
    }

    const escHandler = () => onClose?.();
    escapeStack.push(escHandler);

    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const items = getFocusable(panelRef.current);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    const node = panelRef.current;
    node?.addEventListener("keydown", onKeyDown);

    return () => {
      clearRing();
      node?.removeEventListener("keydown", onKeyDown);
      const i = escapeStack.indexOf(escHandler);
      if (i >= 0) escapeStack.splice(i, 1);
      unlockScroll();
      if (opener && typeof opener.focus === "function") opener.focus({ preventScroll: true });
    };
    // Solo re-ejecuta al activarse/desactivarse; onClose/panelRef son estables en la práctica.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
